/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useEffect, useRef } from 'react';
import { EditorView, keymap, placeholder } from '@codemirror/view';
import { EditorState } from '@codemirror/state';
import { json, jsonParseLinter } from '@codemirror/lang-json';
import { defaultHighlightStyle, syntaxHighlighting, indentUnit, bracketMatching } from '@codemirror/language';
import { linter } from '@codemirror/lint';
import { closeBrackets, closeBracketsKeymap } from '@codemirror/autocomplete';
import { defaultKeymap } from '@codemirror/commands';

import { EditorWrapper } from './JsonEditor.styled';

export const JsonEditor = ({ value, onChange, placeholderText = '', onLint }) => {
  const containerRef = useRef(null);
  const viewRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const state = EditorState.create({
      doc: value,
      extensions: [
        indentUnit.of('  '),
        EditorView.lineWrapping,
        bracketMatching(),
        closeBrackets(),
        keymap.of([...closeBracketsKeymap, ...defaultKeymap]),
        json(),
        syntaxHighlighting(defaultHighlightStyle),
        linter(
          (view) => {
            if (view?.state?.doc?.toString() === '') {
              onLint?.([]);
              return [];
            }
            const diagnostics = jsonParseLinter()(view);
            onLint?.(diagnostics);
            return diagnostics;
          },
          { delay: 300 }
        ),
        EditorView.updateListener.of((update) => {
          if (update.docChanged) {
            onChange(update.state.doc.toString());
          }
        }),
        placeholder(placeholderText)
      ]
    });

    viewRef.current = new EditorView({ state, parent: containerRef.current });
    return () => viewRef.current.destroy();
  }, []);

  useEffect(() => {
    const view = viewRef.current;
    if (view?.state.doc.toString() !== value) {
      view.dispatch({
        changes: { from: 0, to: view.state.doc.length, insert: value }
      });
    }
  }, [value]);

  return <EditorWrapper ref={containerRef} data-test="json-editor" />;
};
